import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import Observable from "zen-observable";

import { setContext } from "@apollo/client/link/context";

import config from "../config/config";
import tokenService from "./tokenService";
import { MessageEdge } from "../features/chat/models/messageEdge";
import { ChatsEdge } from "../features/chat/models/chatsEdge";

// Error codes that mean the access token was missing/expired/invalid and a
// silent refresh should be attempted. UNAUTHENTICATED is emitted by the
// backend when a Bearer token is present but fails validation; UNAUTHORIZED
// when a protected resolver is hit without credentials.
const AUTH_ERROR_CODES = ["UNAUTHORIZED", "UNAUTHENTICATED"];

// GraphQL error codes that mean the refresh token itself was definitively
// rejected (revoked/expired/reused) — see backend's INVALID_REFRESH_TOKEN in
// backend/features/auth/presentation/graphql/error_codes.go. Retrying with
// the same token will never succeed, so on these we must stop retrying and
// clear the stale token instead of leaving it for the next request to retry.
const REFRESH_REJECTED_CODES = ["INVALID_REFRESH_TOKEN", "UNAUTHORIZED", "UNAUTHENTICATED"];

/**
 * Performs one access-token refresh against the GraphQL endpoint using the
 * stored refresh token (or the HttpOnly cookie). Returns true on success.
 * Shared by the reactive errorLink and the proactive authLink below.
 */
const performTokenRefresh = async (): Promise<boolean> => {
  try {
    const refreshToken = await tokenService.getRefreshToken();

    // Attempt refresh: if we have a JS-accessible refresh token, send it;
    // otherwise rely on the HttpOnly cookie being sent with credentials.
    const res = await fetch(config.api.graphql as string, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        query: `mutation RefreshToken($refreshToken: String) { refreshToken(refreshToken: $refreshToken) { accessToken } }`,
        variables: refreshToken ? { refreshToken } : undefined,
      }),
    });

    // Network-level/server failures (5xx, unreachable) are transient — keep
    // the stored token so the next attempt can retry it.
    if (!res.ok && res.status >= 500) return false;

    const payload = await res.json().catch(() => null);

    // A definitive rejection means this refresh token will never work again.
    // Clear it and force logout so we stop hammering the endpoint with the
    // same doomed token on every subsequent request.
    const errorCode = payload?.errors?.[0]?.extensions?.code as string | undefined;
    if (errorCode && REFRESH_REJECTED_CODES.includes(errorCode)) {
      console.warn(`[auth] refresh token rejected (${errorCode}); clearing tokens`);
      await tokenService.clearTokens();
      window.dispatchEvent(
        new CustomEvent("auth-error", {
          detail: { message: "Session expired. Please log in again." },
        })
      );
      return false;
    }

    if (!res.ok) return false;

    const newAccessToken = payload?.data?.refreshToken?.accessToken;
    if (!newAccessToken) return false;

    await tokenService.updateAccessToken(newAccessToken);
    window.dispatchEvent(new CustomEvent("auth-change"));
    return true;
  } catch (e) {
    return false;
  }
};

/** Joins an in-flight refresh if one exists, otherwise starts a new one. */
const refreshAccessToken = (): Promise<boolean> => {
  const inFlight = tokenService.getRefreshPromise();
  if (inFlight) return inFlight;
  return tokenService.startRefresh(performTokenRefresh);
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      if (AUTH_ERROR_CODES.includes(err.extensions?.code as string)) {
        console.error("[GraphQL call] Unauthorized error:", err);

        if (window.__ignoreAuthErrors) {
          console.log("Ignoring auth error during login grace period");
          return;
        }

        // If this is a login/refresh operation, don't attempt to refresh
        const operationName = operation.operationName;
        if (["Authenticate", "RefreshToken"].includes(operationName)) {
          return;
        }

        // Return an Observable that waits for refresh and retries the operation
  return (new Observable((observer: any) => {
          const doRetry = async () => {
            try {
              const success = await refreshAccessToken();

              if (success) {
                // Retry the original operation
                const subscriber = forward ? forward(operation).subscribe({
                  next: (v) => observer.next(v),
                  error: (e) => observer.error(e),
                  complete: () => observer.complete(),
                }) : null;

                // teardown logic
                return () => {
                  try { subscriber?.unsubscribe(); } catch (e) { /* ignore */ }
                };
              }

              // refresh failed -> dispatch auth-error to trigger app logout
              window.dispatchEvent(
                new CustomEvent("auth-error", {
                  detail: { message: "Session expired. Please log in again." },
                })
              );
              observer.error(new Error("Unauthorized and refresh failed"));
            } catch (e) {
              observer.error(e as any);
            }
          };

          // start retry flow
          doRetry();
  }) as any);
      }
    }
  }

  if (networkError) {
    console.error("Network error:", networkError);
  }
});

const httpLink = createHttpLink({
  uri: config.api.graphql,
  // Send cookies (HttpOnly refresh cookie) with GraphQL requests so server can read refresh_token cookie
  credentials: "include",
});

const authLink = setContext(async (operation, { headers }) => {
  try {
    let accessToken = await tokenService.getAccessToken();

    // Proactive refresh: if the stored access token is expired, refresh it
    // before the request goes out so the server never sees a stale token.
    // The reactive errorLink stays as a safety net. Never recurse on the
    // refresh/login operations themselves.
    const isAuthOperation = ["Authenticate", "RefreshToken"].includes(
      operation.operationName ?? ""
    );
    if (accessToken && !isAuthOperation && !tokenService.isTokenValid(accessToken)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        accessToken = await tokenService.getAccessToken();
      }
    }

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : "",
      },
    };
  } catch (err) {
    console.error("Error in auth link:", err);
    return { headers };
  }
});

export interface LifecycleAwareApolloClient
  extends ApolloClient<NormalizedCacheObject> {
  lifecycleState: {
    status: "normal" | "resumed" | "notification-launch";
    lastActiveTime: number;
    chatId?: string | null;
    messageId?: string | null;
  };

  notifyAppLaunchedFromNotification(chatId?: string, messageId?: string): void;
}

const cache = new InMemoryCache({
  typePolicies: {
    Message: {
      keyFields: ["id"],
      fields: {
        readBy: {
          // Custom merge function for readBy field
          merge(existing = [], incoming = []) {
            // If incoming is empty, return existing
            if (!incoming || !Array.isArray(incoming)) return existing || [];
            if (incoming.length === 0) return existing || [];

            // Create a map of existing read statuses by userId
            const existingMap = new Map();
            if (Array.isArray(existing)) {
              existing.forEach((item) => {
                if (item && item.userId) {
                  existingMap.set(item.userId, item);
                }
              });
            }

            // Create a new array with all unique read statuses
            const merged = Array.isArray(existing) ? [...existing] : [];

            // Add any new read statuses
            incoming.forEach((item) => {
              if (item && item.userId && !existingMap.has(item.userId)) {
                merged.push(item);
              }
            });

            return merged;
          },
        },
      },
    },
    Query: {
      fields: {
        messages: {
          keyArgs: ["chatId", "sortDirection"],
          merge(existing = { edges: [] }, incoming, { args }) {
            const existingIds = new Set(
              existing.edges.map((edge: MessageEdge) => edge.node.id)
            );
            const newEdges = incoming.edges.filter(
              (edge: MessageEdge) => !existingIds.has(edge.node.id)
            );

            // Access sortDirection from the query variables
            const sortDirection = args?.sortDirection || "DESC";
            
            // For DESC order (newest first), add new edges at the start
            return {
              ...incoming,
              edges:
                sortDirection === "DESC"
                  ? [...newEdges, ...existing.edges]
                  : [...existing.edges, ...newEdges],
              pageInfo: incoming.pageInfo,
            };
          },
        },
        chats: {
          keyArgs: ["search"],
          merge(existing, incoming, { variables }) {
            const cursor = variables?.cursor;
            
            // If this is a fresh query (no cursor), replace entirely
            if (!cursor) {
              return incoming;
            }
            
            // This is a fetchMore with a cursor - append to existing
            if (!existing) {
              return incoming;
            }
            
            const existingEdges = existing.edges || [];
            const incomingEdges = incoming.edges || [];
            
            // Deduplicate by cursor
            const existingCursors = new Set(
              existingEdges.map((edge: ChatsEdge) => edge.cursor)
            );
            const newEdges = incomingEdges.filter(
              (edge: ChatsEdge) => !existingCursors.has(edge.cursor)
            );
            
            return {
              ...incoming,
              edges: [...existingEdges, ...newEdges],
              pageInfo: incoming.pageInfo,
            };
          },
        },
      },
    },

    CopyEventResponse: {
      keyFields: false,
    },
    Event: {
      keyFields: ["id"],
    },
    EventSubscriber: {
      keyFields: ["id"],
    },
    Chat: {
      keyFields: ["id"],
      fields: {},
    },
    ChatParticipant: {
      keyFields: ["userId"],
      fields: {
        // Add this field policy to preserve avatarUrl
        avatarUrl: {
          // Only accept values that look like URLs
          merge(existing, incoming) {
            // If incoming is empty or doesn't look like a URL, keep existing
            if (
              !incoming ||
              (typeof incoming === "string" && !incoming.startsWith("http"))
            ) {
              return existing;
            }
            return incoming;
          },
        },
      },
    },
  },
});

const baseClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: cache,
  connectToDevTools: config.env === "development",
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});

const graphQLClient = baseClient as LifecycleAwareApolloClient;

// Add lifecycle state
graphQLClient.lifecycleState = {
  status: "normal",
  lastActiveTime: Date.now(),
};

// Add lifecycle methods
graphQLClient.notifyAppLaunchedFromNotification = (
  chatId?: string,
  messageId?: string
) => {
  graphQLClient.lifecycleState.status = "notification-launch";
  graphQLClient.lifecycleState.chatId = chatId;
  graphQLClient.lifecycleState.messageId = messageId;
  graphQLClient.lifecycleState.lastActiveTime = Date.now();

  // Invalidate chat query cache if we have a chatId
  if (chatId) {
    // Evict just the messages for this chat
    graphQLClient.cache.evict({
      fieldName: "messages",
      args: { chatId },
    });

    // Optional: evict the chat itself to get fresh data
    graphQLClient.cache.evict({
      id: `Chat:${chatId}`,
    });

    graphQLClient.cache.gc();
  }
};

export default graphQLClient;
